import type { ReactNode } from "react";

const SAFE_PROTO = /^(https?:|mailto:|tel:)/i;

/**
 * Render a Google Calendar event description (HTML-as-string) as React nodes,
 * keeping a small whitelist of safe tags (br, a, b/strong, i/em, p, ul/ol/li)
 * and stripping the rest. Hrefs are validated to http(s) / mailto / tel only.
 */
export function formatDescription(html: string): ReactNode {
  if (typeof window === "undefined") {
    // SSR fallback: return text with tags stripped
    return html.replace(/<[^>]*>/g, "");
  }
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstChild as Element | null;
  if (!root) return null;
  return renderChildren(root);
}

function renderChildren(parent: Node): ReactNode[] {
  return Array.from(parent.childNodes).map((child, i) => renderNode(child, i));
}

function renderNode(node: Node, key: number): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }
  const el = node as Element;
  const tag = el.tagName.toLowerCase();

  switch (tag) {
    case "br":
      return <br key={key} />;
    case "a": {
      const href = el.getAttribute("href") ?? "";
      if (!SAFE_PROTO.test(href)) {
        return <span key={key}>{renderChildren(el)}</span>;
      }
      const isExternal = /^https?:/i.test(href);
      return (
        <a
          key={key}
          href={href}
          {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        >
          {renderChildren(el)}
        </a>
      );
    }
    case "b":
    case "strong":
      return <strong key={key}>{renderChildren(el)}</strong>;
    case "i":
    case "em":
      return <em key={key}>{renderChildren(el)}</em>;
    case "p":
      return <p key={key}>{renderChildren(el)}</p>;
    case "ul":
      return <ul key={key}>{renderChildren(el)}</ul>;
    case "ol":
      return <ol key={key}>{renderChildren(el)}</ol>;
    case "li":
      return <li key={key}>{renderChildren(el)}</li>;
    default:
      return <span key={key}>{renderChildren(el)}</span>;
  }
}
